import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useStagingMode } from "@/hooks/useStagingMode";
import { useCsqLevels } from "./levels";

type Uxa = { push: (cmd: unknown[]) => void };
// The ContentSquare tag is installed outside PODGO; `_uxa` queues commands until it loads.
const uxa = (): Uxa => {
  const w = window as unknown as { _uxa?: Uxa };
  w._uxa = w._uxa || ([] as unknown as Uxa);
  return w._uxa;
};

/**
 * Sends audience attributes and first-time milestones to ContentSquare.
 * Skipped for admins, demo accounts, impersonation and Staging Mode, so the
 * same exclusions as the database apply in the browser too.
 */
export function useContentsquareTracking() {
  const { user, profile, roles, subscription, isImpersonating } = useAuth();
  const { isStaging } = useStagingMode();
  const { data: levels } = useCsqLevels();
  const { pathname } = useLocation();
  const sending = useRef(false);

  const isAdmin = roles.includes("admin");
  const excluded = !user || isAdmin || !!profile?.is_demo || isImpersonating || isStaging;

  // Audience attributes (slots 1-5). Never personal data.
  useEffect(() => {
    const role = !user ? "guest" : isAdmin ? "podgo_admin" : roles.includes("network_admin") ? "network_admin" : "podcaster";
    const plan = !user ? "none" : subscription?.manual_override_plan_key ?? subscription?.plan ?? "none";
    const attrs: [string, string][] = [
      ["podgo_role", role],
      ["podgo_plan", plan],
      ["podgo_is_demo", String(!!profile?.is_demo)],
      ["podgo_is_test", String(!!profile?.is_demo)],
      ["podgo_is_impersonating", String(isImpersonating)],
    ];
    attrs.forEach(([k, v], i) => uxa().push(["setCustomVariable", i + 1, k, v, 3]));
  }, [user, isAdmin, roles, subscription, profile?.is_demo, isImpersonating]);

  // Page-visit levels + any unsent milestones, checked on each route change.
  useEffect(() => {
    if (excluded || !levels?.length || sending.current) return;
    sending.current = true;
    (async () => {
      try {
        if (levels.some((l) => l.is_enabled && l.trigger_type === "page")) {
          await supabase.rpc("record_page_level" as never, { _path: pathname } as never);
        }
        const { data } = await supabase
          .from("user_level_achievements" as never)
          .select("id, level_id")
          .eq("user_id", user!.id)
          .eq("reported", false);
        const rows = (data ?? []) as unknown as { id: string; level_id: string }[];
        if (!rows.length) return;
        const paths = rows
          .map((r) => levels.find((l) => l.id === r.level_id))
          .filter((l) => l?.send_to_csq)
          .map((l) => l!.csq_path);
        paths.forEach((p) => uxa().push(["trackPageview", p]));
        // Restore the real page so later interactions are attributed to it.
        if (paths.length) uxa().push(["trackPageview", pathname + window.location.search]);
        await supabase.rpc("mark_levels_reported" as never, { _ids: rows.map((r) => r.id) } as never);
      } finally {
        sending.current = false;
      }
    })();
  }, [pathname, excluded, levels, user]);
}
